import { useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ENTITY_TYPES } from '@sales-center/common'
import { toast, useIsUnmounted } from '@sales-center/components'

import { useAppDispatch } from '@user/store/hooks'
import { commitAppointment, getAppointment } from '@user/store/scheduling'
import { removeLeadFromList } from '@user/store/lead'

import {
  closeActionCreator as close,
  commitActionCreator as commit,
  useSchedulingWizard,
  useSchedulingWizardCallbacks,
} from '../../wizardContext'
import { useLeadScheduleAutoClose } from '../../hooks/useLeadScheduleAutoClose'
import type { CommitHandler } from '../typings'
import { isAPIError } from '@user/store/services/crud'
import { useCloseWinOpportunity } from '@user/store/opportunity'
import { useSelectOppStages } from '@user/store/salesFunnel'
import { externalAppTimeoutError } from '@user/store/utilities/functions'

export function useCommitResult() {
  const appDispatch = useAppDispatch()
  const isUnmounted = useIsUnmounted()
  const navigate = useNavigate()
  const location = useLocation()

  const [state, dispatch] = useSchedulingWizard()
  const { onLeadAutoClosed } = useSchedulingWizardCallbacks()

  const {
    mode,
    entityId,
    entityType,
    appointment,
    closeOnCommit,
    recurringServices = false,
    bestFitInput: { serviceIds = [] },
    salesFunnelId = -1,
  } = state

  const [, closedWonStageId] = useSelectOppStages(salesFunnelId)

  const { onCloseWin } = useCloseWinOpportunity(entityId)

  const { willAutoClose: leadWillAutoClose } = useLeadScheduleAutoClose()

  const hasAppointment = !!appointment

  const commitResult = useCallback<CommitHandler>(
    async (resultId) => {
      dispatch(commit(true))

      const appointmentType =
        mode === 'service'
          ? recurringServices
            ? 'Initial'
            : 'OneTime'
          : 'Estimate'

      const billingType = !mode || mode === 'service' ? 'paid' : mode

      const result = await appDispatch(
        commitAppointment({
          resultId,
          input: {
            entityId,
            entityType,
            serviceIds,
            billingType,
            appointmentType,
          },
        }),
      )

      if (!isUnmounted()) {
        dispatch(commit(false))

        if (commitAppointment.fulfilled.match(result)) {
          const { appointmentId } = result.payload

          const leadWasAutoClosed =
            entityType === ENTITY_TYPES.Lead && leadWillAutoClose

          if (!leadWasAutoClosed) {
            appDispatch(
              getAppointment({
                entityId,
                entityType,
                appointmentId,
              }),
            )
          }

          if (hasAppointment) toast.success('Appointment rescheduled')
          else toast.success('Appointment scheduled')

          if (closeOnCommit && entityType === ENTITY_TYPES.Opportunity) {
            await onCloseWin({
              opportunityId: entityId,
              closedWonFunnelStageId: closedWonStageId,
              salesNote: '',
              appData: [],
            })
          }

          dispatch(close())

          if (leadWasAutoClosed) {
            toast.success('Lead was automatically closed/won.')

            if (location.pathname === `/leads/${entityId}`) {
              navigate('/leads')
            }

            appDispatch(removeLeadFromList(entityId))

            onLeadAutoClosed?.()
          }
        } else {
          const { payload } = result

          if (commitAppointment.rejected.match(result) && isAPIError(payload)) {
            if (!externalAppTimeoutError(payload)) {
              toast.error(payload.Message)
            }
          } else {
            toast.error('Unexpected error')
          }
        }
      }
    },
    [
      mode,
      navigate,
      entityId,
      dispatch,
      onCloseWin,
      serviceIds,
      entityType,
      appDispatch,
      isUnmounted,
      closeOnCommit,
      hasAppointment,
      onLeadAutoClosed,
      location.pathname,
      closedWonStageId,
      recurringServices,
      leadWillAutoClose,
    ],
  )

  return commitResult
}
